/**
 * /logistica/instalacoes/:id — Detalhe da OI com actions de transicao.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiPlay,
  FiRefreshCw,
  FiTrash2,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  cancelarOi,
  concluirOi,
  iniciarOi,
  obterOi,
  reagendarOi,
  SITUACAO_OI_LABELS,
  SITUACAO_OI_TONES,
} from "../../../services/logistica";
import { PageContainer } from "../../../styles/shared";

import { fmtData } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  SectionTitle,
  Textarea,
} from "../../Orcamentos/styles";


export default function InstalacaoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [oi, setOi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [texto, setTexto] = useState("");

  const podeInstalar = isMaster || hasAllPermissions(["logistica:instalar"]);
  const podePlanejar = isMaster || hasAllPermissions(["logistica:planejar"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await obterOi(request, id);
      setOi(d);
    } catch (err) {
      setError(err?.message || "Erro.");
    } finally { setLoading(false); }
  }, [request, id]);

  useEffect(() => { carregar(); }, [carregar]);

  const executar = useCallback(async () => {
    if (!oi || !confirmando) return;
    let promise;
    if (confirmando === "iniciar") promise = iniciarOi(request, id);
    else if (confirmando === "concluir") promise = concluirOi(request, id, { observacao: texto });
    else if (confirmando === "reagendar") {
      if (!texto.trim()) throw new Error("Motivo obrigatório.");
      promise = reagendarOi(request, id, { motivo: texto });
    } else if (confirmando === "cancelar") {
      if (!texto.trim()) throw new Error("Motivo obrigatório.");
      promise = cancelarOi(request, id, { motivo: texto });
    }
    if (promise) {
      const d = await promise;
      setOi(d); setConfirmando(null); setTexto("");
    }
  }, [confirmando, texto, request, id, oi]);

  if (loading && !oi) return <PageContainer><EmptyMsg>Carregando...</EmptyMsg></PageContainer>;
  if (!oi) {
    return (
      <PageContainer>
        <EntityHeader title="Erro" actions={[{
          label: "Voltar", icon: <FiArrowLeft size={14} />,
          onClick: () => navigate("/logistica/instalacoes"),
        }]} />
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </PageContainer>
    );
  }

  const actions = [
    { label: "Voltar", icon: <FiArrowLeft size={14} />, onClick: () => navigate("/logistica/instalacoes") },
    { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
  ];
  if (oi.situacao === "AGENDADA" && podeInstalar) {
    actions.push({ label: "Iniciar", icon: <FiPlay size={14} />, onClick: () => setConfirmando("iniciar"), primary: true });
  }
  if (oi.situacao === "EM_EXECUCAO" && podeInstalar) {
    actions.push({ label: "Concluir", icon: <FiCheckCircle size={14} />, onClick: () => setConfirmando("concluir"), primary: true });
  }
  if (["AGENDADA", "EM_EXECUCAO"].includes(oi.situacao) && podeInstalar) {
    actions.push({ label: "Reagendar", icon: <FiCalendar size={14} />, onClick: () => setConfirmando("reagendar") });
  }
  if (["AGENDADA", "EM_EXECUCAO"].includes(oi.situacao) && podePlanejar) {
    actions.push({ label: "Cancelar", icon: <FiTrash2 size={14} />, onClick: () => setConfirmando("cancelar") });
  }

  const kpis = [
    { label: "Pedido", value: oi.pedido_numero, sub: oi.pedido_cliente_nome, tone: "neutral" },
    { label: "Equipe", value: oi.equipe_nome, sub: oi.equipe_lider || "", tone: "neutral" },
    { label: "Data", value: oi.data_agendada, tone: "neutral" },
    {
      label: "Situação",
      value: SITUACAO_OI_LABELS[oi.situacao] || oi.situacao,
      tone: SITUACAO_OI_TONES[oi.situacao] || "neutral",
    },
  ];

  return (
    <PageContainer>
      <EntityHeader
        title={oi.numero}
        subtitle={`Instalação de ${oi.pedido_numero}`}
        badges={[{
          label: SITUACAO_OI_LABELS[oi.situacao] || oi.situacao,
          tone: SITUACAO_OI_TONES[oi.situacao] || "neutral",
        }]}
        actions={actions}
      />

      <KpiStrip items={kpis} />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <Card>
        <SectionTitle>Dados</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
          <div><strong>Pedido:</strong> {oi.pedido_numero}</div>
          <div><strong>Cliente:</strong> {oi.pedido_cliente_nome}</div>
          <div><strong>Equipe:</strong> {oi.equipe_nome}</div>
          {oi.equipe_lider && <div><strong>Líder:</strong> {oi.equipe_lider}</div>}
          {oi.equipe_pessoas?.length > 0 && (
            <div><strong>Pessoas:</strong> {oi.equipe_pessoas.join(", ")}</div>
          )}
          <div><strong>Data:</strong> {oi.data_agendada}</div>
          {oi.janela_inicio && oi.janela_fim && (
            <div><strong>Janela:</strong> {oi.janela_inicio} - {oi.janela_fim}</div>
          )}
          <div><strong>Endereço:</strong> {oi.endereco}</div>
          {oi.telefone_contato && <div><strong>Contato:</strong> {oi.telefone_contato}</div>}
          {oi.observacoes && <div><strong>Observações:</strong> {oi.observacoes}</div>}
          <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "4px 0" }} />
          {oi.iniciada_em && <div><strong>Iniciada em:</strong> {fmtData(oi.iniciada_em)}</div>}
          {oi.concluida_em && (
            <>
              <div><strong>Concluída em:</strong> {fmtData(oi.concluida_em)}</div>
              {oi.observacao_conclusao && (
                <div><strong>Observação:</strong> {oi.observacao_conclusao}</div>
              )}
            </>
          )}
          {oi.reagendada_em && (
            <>
              <div><strong>Reagendada em:</strong> {fmtData(oi.reagendada_em)}</div>
              <div><strong>Motivo:</strong> {oi.motivo_reagendamento}</div>
            </>
          )}
          {oi.cancelada_em && (
            <>
              <div><strong>Cancelada em:</strong> {fmtData(oi.cancelada_em)}</div>
              <div><strong>Motivo:</strong> {oi.motivo_cancelamento}</div>
            </>
          )}
        </div>
      </Card>

      {confirmando && (
        <SlideOverPanel
          open
          onClose={() => { setConfirmando(null); setTexto(""); }}
          title={({
            iniciar: "Iniciar instalação",
            concluir: "Concluir instalação",
            reagendar: "Reagendar instalação",
            cancelar: "Cancelar instalação",
          })[confirmando]}
          footer={
            <AsyncButton
              variant={
                confirmando === "cancelar" || confirmando === "reagendar"
                  ? "danger" : "primary"
              }
              onClick={executar}
              successMessage="Ação executada"
            >
              Confirmar
            </AsyncButton>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(confirmando === "reagendar" || confirmando === "cancelar") && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Motivo *</span>
                <Textarea
                  rows={3} value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Obrigatório"
                  maxLength={300}
                />
              </label>
            )}
            {confirmando === "concluir" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Observação (opcional)</span>
                <Textarea
                  rows={3} value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  maxLength={1000}
                />
              </label>
            )}
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {confirmando === "iniciar" && "Equipe está no cliente fazendo. Marca OI como EM_EXECUCAO."}
              {confirmando === "concluir" && "Marca OI como CONCLUIDA. Terminal."}
              {confirmando === "reagendar" && "Equipe foi mas não conseguiu. Para reagendar, crie nova OI manualmente."}
              {confirmando === "cancelar" && "Aborta OI. Cliente desistiu ou pedido cancelado."}
            </div>
          </div>
        </SlideOverPanel>
      )}
    </PageContainer>
  );
}
